import { useNavigate, useParams } from "react-router-dom";
import ButtonComponent from "../../../../components/button/button";
import { useState, useEffect } from "react";
import InputComponent from "../../../../components/input/input";
import { getMe, getUserById, updateUser } from "../../../../features/search/service/user-service";
import { getNonSuperAdminRoles, getRoles, getRoleById } from "../../../../features/search/service/role-service";
import { getCompaniesListSelect } from "../../../../features/search/service/company-service";
import { SelectOption } from "../../../../shared/types/select-options";
import { User } from "../../../../shared/types/user";
import { Role } from "../../../../shared/types/role";
import SelectComponent from "../../../../components/input/select";
import { MenuTop } from '../../../../components/menu/top/menu-top';
import './styles/edit.css';
import ButtonSecondaryComponent from "../../../../components/button-secondary/button";
import { ROLES } from "../../../../shared/consts/roles";

export const AdminUsersEdit = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    
    const [name, setName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [roleId, setRoleId] = useState<string>('');
    const [companyId, setCompanyId] = useState<string>('');
    const [me, setMe] = useState<User | null>(null);
    const [meRole, setMeRole] = useState<Role | null>(null);
    const [userToEdit, setUserToEdit] = useState<User | null>(null);
    const [roles, setRoles] = useState<SelectOption[]>([]);
    const [companies, setCompanies] = useState<SelectOption[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [fetchingUser, setFetchingUser] = useState<boolean>(true);

    // Buscar dados do usuário logado
    useEffect(() => {
        const fetchUser = async () => {
            try {
                const userData = await getMe();
                setMe(userData);

                // Buscar a role do usuário logado
                if (userData.role_id) {
                    const roleData = await getRoleById(userData.role_id);
                    setMeRole(roleData);
                }
            } catch (error) {
                console.error('❌ Erro ao buscar dados do usuário:', error);
            }
        };
        fetchUser();
    }, []);

    // Buscar dados do usuário a ser editado
    useEffect(() => {
        const fetchUserToEdit = async () => {
            if (!id) {
                navigate('/admin-painel');
                return;
            }

            try {
                setFetchingUser(true);
                const userData = await getUserById(id);
                
                if (!userData) {
                    console.error('❌ Usuário não encontrado');
                    navigate('/admin-painel');
                    return;
                }

                setUserToEdit(userData);
                setName(userData.name || '');
                setEmail(userData.email || '');
                setRoleId(userData.role_id ? String(userData.role_id) : '');
                setCompanyId(userData.companyId ? String(userData.companyId) : '');
            } catch (error) {
                console.error('❌ Erro ao buscar usuário para editar:', error);
                navigate('/admin-painel');
            } finally {
                setFetchingUser(false);
            }
        };

        fetchUserToEdit();
    }, [id, navigate]);

    // Buscar roles e empresas disponíveis
    useEffect(() => {
        const fetchRolesAndCompanies = async () => {
            if (!me || !meRole) {
                return;
            }

            try {
                let rolesData: SelectOption[] = [];
                let companiesData: SelectOption[] = [];

                const meRoleDescription = meRole.description;

                if (meRoleDescription === ROLES.SUPER_ADMIN) {
                    [rolesData, companiesData] = await Promise.all([
                        getRoles(),
                        getCompaniesListSelect()
                    ]);
                } else if (meRoleDescription === ROLES.ADMIN) {
                    [rolesData, companiesData] = await Promise.all([
                        getNonSuperAdminRoles(),
                        getCompaniesListSelect()
                    ]);
                }
                
                setRoles(rolesData);
                setCompanies(companiesData);

                // Se roleId ainda não foi definido, define o primeiro
                if (!roleId && rolesData.length > 0) {
                    setRoleId(String(rolesData[0].id));
                }

                if (!companyId && companiesData.length > 0) {
                    setCompanyId(String(companiesData[0].id));
                }

            } catch (error) {
                console.error('❌ Erro ao buscar roles/empresas:', error);
            }
        };

        fetchRolesAndCompanies();
    }, [me, meRole]);

    const handleUpdateUser = async () => {
        if (!id) {
            console.error('❌ ID do usuário não fornecido');
            return;
        }

        if (!name || name.trim() === '') {
            alert('Nome é obrigatório');
            return;
        }

        if (!email || email.trim() === '') {
            alert('Email é obrigatório');
            return;
        }

        if (!roleId || roleId === '' || roleId === 'undefined') {
            alert('Função é obrigatória');
            return;
        }

        if (!me?.companyId && !companyId) {
            alert('Empresa é obrigatória');
            return;
        }

        const payload: any = {
            name: name.trim(),
            email: email.trim(),
            roleId: roleId,
            companyId: companyId || me?.companyId,
        };

        setLoading(true);

        try {
            const updatedUser = await updateUser(id, payload);
            
            if (updatedUser) {
                navigate('/admin-painel/users');
            }
            
        } catch (error) {
            console.error('❌ Erro ao atualizar usuário:', error);
            alert('Erro ao atualizar usuário. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate('/admin-painel/users');
    };

    if (fetchingUser) {
        return (
            <div>
                <MenuTop />
                <div className="edit-user-container">
                    <p style={{ color: '#666', fontSize: '16px' }}>
                        Carregando dados do usuário...
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div>
            <MenuTop />
            <div className="edit-user-container">
                <h1>Editar Usuário</h1>

                <InputComponent
                    id="id-input-name"
                    label="Nome"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                />
                
                <InputComponent
                    id="id-input-email"
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                />

                {meRole?.description === ROLES.SUPER_ADMIN && companies.length > 0 && (
                    <SelectComponent
                        label="Empresa"
                        value={companyId || ''}
                        onChange={(value) => {
                            setCompanyId(String(value));
                        }}
                        options={companies}
                        styles={{ marginTop: '10px' }}
                    />
                )}

                {roles.length > 0 ? (
                    <SelectComponent
                        label="Função"
                        value={roleId || ''}
                        onChange={(value) => {
                            setRoleId(String(value));
                        }}
                        options={roles}
                        styles={{ marginTop: '10px' }}
                    />
                ) : (
                    <p style={{ color: '#666', fontSize: '14px', marginTop: '10px' }}>
                        Carregando funções...
                    </p>
                )}

                <div className="button-container">
                    <ButtonSecondaryComponent
                        label="Cancelar"
                        onClick={handleCancel}
                        disabled={loading}
                    />
                    <ButtonComponent
                        label={loading ? "Salvando..." : "Salvar Alterações"}
                        onClick={handleUpdateUser}
                        disabled={loading || !roleId || roleId === '' || roleId === 'undefined'}
                    />
                </div>
            </div>
        </div>
    );
};